import { gtcDebounce as debounce, gtcHandleize as handleize } from './gtc-utils.js';

class AllIngredients extends HTMLElement {
  connectedCallback() {
    this.pageSize = 12;

    this.search = this.querySelector('[data-search]');
    this.alphabetLetters = this.querySelectorAll('[data-alphabet-letter]');
    this.list = this.querySelector('[data-list]');

    this.alphabetItemActiveClass = 'all-ingredients__alphabet-item--active';

    this.init = this.init.bind(this);
    this.initSearch = this.initSearch.bind(this);
    this.initAlphabet = this.initAlphabet.bind(this);
    this.paginateItems = this.paginateItems.bind(this);
    this.renderItems = this.renderItems.bind(this);
    this.renderPagination = this.renderPagination.bind(this);
    this.renderEmpty = this.renderEmpty.bind(this);

    this.init();
  }

  async init() {
    const response = await fetch('?view=api-ingredients');
    const json = await response.json();

    this.allItems = new Array(...json);
    this.filteredItems = new Array(...this.allItems);

    this.paginateItems();
    this.initSearch();
    this.initAlphabet();

    await this.renderItems();
  }

  initSearch() {
    this.search.addEventListener('input', debounce(() => {
      this.alphabetLetters.forEach((letter) => {
        letter.classList.remove(this.alphabetItemActiveClass);
      });

      this.filterItems(this.search.value);
    }, 300));
  }

  initAlphabet() {
    this.alphabetLetters.forEach((letter) => {
      letter.disabled = this.allItems.find((item) =>
        item.name?.toLowerCase().startsWith(letter.dataset.letter.toLowerCase())
      ) === undefined;

      letter.addEventListener('click', () => {
        if (letter.classList.contains(this.alphabetItemActiveClass)) {
          letter.classList.remove(this.alphabetItemActiveClass);
          this.filterItems();
        } else {
          this.search.value = null;

          this.alphabetLetters.forEach((otherLetter) => {
            otherLetter.classList.toggle(this.alphabetItemActiveClass, otherLetter === letter);
          });

          this.filterItems(letter.dataset.letter.toLowerCase());
        }
      });
    });
  }

  async filterItems(str = '') {
    this.filteredItems = str.length
      ? this.allItems.filter(item => {
        const query = str.toLowerCase();
        const matchesName = item.name?.toLowerCase()[query.length > 2 ? 'includes' : 'startsWith'](query);
        const matchesScientificName = item.scientificName?.toLowerCase()[query.length > 2 ? 'includes' : 'startsWith'](query);
        const matchesTag = query.length > 2 ? item.tags.join(' ').toLowerCase().includes(query) : false;
        const matchesDescription = query.length > 2 ? item.description?.toLowerCase().includes(query) : false;

        return matchesName || matchesScientificName || matchesTag || matchesDescription;
      })
      : new Array(...this.allItems);

    await new Promise((resolve) => setTimeout(resolve, 280));

    this.paginateItems();

    if (this.paginatedItems.length) {
      await this.renderItems();
    } else {
      this.renderEmpty(str);
    }
  }

  paginateItems() {
    this.currentPage = 0;
    this.paginatedItems = new Array();

    const items = new Array(...this.filteredItems);

    while (items.length) {
      this.paginatedItems.push(items.splice(0, this.pageSize));
    }
  }

  async renderItems() {
    const itemsHtml = await Promise.all(this.paginatedItems[this.currentPage].map(this.getItemHtml));

    if (this.currentPage === 0) {
      this.list.innerHTML = '';
    }

    this.list.insertAdjacentHTML('beforeend', itemsHtml.join(''));

    await this.renderPagination();
  }

  async renderPagination() {
    this.pagination?.remove();

    const viewedItems = Math.min(this.currentPage * this.pageSize + this.pageSize, this.filteredItems.length);
    const viewedPercentage = Math.ceil(viewedItems / this.filteredItems.length * 100);

    this.pagination = document.createElement('div');
    this.pagination.classList.add('all-ingredients__pagination');

    this.pagination.innerHTML = `
      <p class="all-ingredients__pagination__text">
        ${window.themeVariables.strings.viewingXOfY.replace('{{ count }}', viewedItems).replace('{{ total }}', this.filteredItems.length)}
      </p>

      <div class="all-ingredients__pagination__progress">
        <div class="all-ingredients__pagination__progress-fill" style="width: ${viewedPercentage}%"></div>
      </div>
    `;

    this.list.insertAdjacentElement('afterend', this.pagination);

    if (this.currentPage < this.paginatedItems.length - 1) {
      const next = document.createElement('button');
      next.type = 'button';
      next.classList.add('button', 'button--lg', 'button--outline');
      next.textContent = window.themeVariables.strings.loadMore;

      this.pagination.insertAdjacentElement('beforeend', next);

      next.addEventListener('click', async () => {
        this.currentPage++;
        await this.renderItems();
      });
    }
  }

  renderEmpty(query = '') {
    this.list.innerHTML = `
      <li class="all-ingredients__no-results">
        ${window.themeVariables.strings.noResults.replace('{{ query }}', query)}
      </li>
    `;

    this.pagination?.remove();
  }

  async getItemHtml(item) {
    const drawerId = `${item.handle}-drawer`;
    const carouselId = item.productCollection !== null ? `carousel-${drawerId}` : null;

    let collectionItemsHtml = null;

    if (item.productCollection !== null) {
      const response = await fetch(`/collections/${item.productCollection.handle}?view=api-items`);
      collectionItemsHtml = await response.text();
    }

    return `
      <li class="ingredient-item">
        <p class="ingredient-item__title">${item.name}</p>

        ${item.image !== null || item.tags.length
          ? `
              <div class="ingredient-item__hero">
                ${item.image !== null
                  ? `
                      <div class="ingredient-item__image">
                        <img src="${item.image.src}" ${item.image.alt ? `alt="${item.image.alt}"` : ''}>
                      </div>
                    `
                  : ''
                }

                ${item.tags.length
                  ? `
                      <div class="ingredient-item__tags">
                        ${item.tags.map(tag => `<span class="ingredient-item__tag">${tag}</span>`).join('')}
                      </div>
                    `
                  : ''
                }
              </div>
            `
          : ''
        }

        ${item.description != null
          ? `
              <div class="ingredient-item__description prose">${item.description}</div>
            `
          : ''
        }

        <div class="ingredient-item__footer">
          <button type="button" aria-controls=${drawerId} class="ingredient-item__button">
            <span>${window.themeVariables.strings.readMore}</span>
            ${window.themeVariables.icons.plusCircle}
          </button>
        </div>

        <x-drawer id="${drawerId}" class="drawer ingredient-drawer">
          <p class="h4" slot="header">${item.name}</p>

          ${item.image !== null || item.tags.length
            ? `
                <div class="ingredient-drawer__hero">
                  ${item.image !== null
                    ? `
                        <div class="ingredient-drawer__image">
                          <image src="${item.image.src}" loading="lazy" ${item.image.alt !== null ? `alt="${item.image.alt}"` : ''}>
                        </div>
                      `
                    : ''
                  }

                  ${item.tags.length
                    ? `
                        <div class="ingredient-drawer__tags">
                          ${item.tags.map((tag) => `<span class="ingredient-drawer__tag">${tag}</span>`).join('')}
                        </div>
                      `
                    : ''
                  }
                </div>
              `
            : ''
          }

          ${item.scientificName !== null
            ? `
                <p class="ingredient-drawer__heading">${window.themeVariables.strings.scientificName}</p>
                <p class="ingredient-drawer__text">${item.scientificName}</p>
              `
            : ''
          }

          ${item.description !== null
            ? `
                <p class="ingredient-drawer__heading">${window.themeVariables.strings.description}</p>
                <div class="ingredient-drawer__text prose">${item.description}</div>
              `
            : ''
          }

          ${item.sellingPoints.length
            ? `
                <div class="ingredient-drawer__selling-points">
                  <p class="ingredient-drawer__heading ingredient-drawer__selling-points__heading">
                    ${window.themeVariables.strings.sellingPoints}
                  </p>

                  <ul class="ingredient-drawer__selling-points__list">
                    ${item.sellingPoints.map((sellingPoint) =>
                      `<li class="ingredient-drawer__selling-points__item">${sellingPoint}</li>`
                    ).join('')}
                  </ul>
                </div>
              `
            : ''
          }

          ${item.scientificDescription !== null
            ? `
                <accordion-disclosure class="accordion">
                  <details class="accordion__disclosure group" aria-expanded="false">
                    <summary>
                      <span class="accordion__toggle h6">
                        <span>${window.themeVariables.strings.scientificDescription}</span>
                        <span class="animated-plus group-expanded:rotate" aria-hidden="true"></span>
                      </span>
                    </summary>

                    <div class="accordion__content prose">
                      ${item.scientificDescription}
                    </div>
                  </details>
                </accordion-disclosure>
              `
            : ''
          }

          ${item.productCollection !== null
            ? `
                <p class="ingredient-drawer__heading">${window.themeVariables.strings.includedIn}</p>

                <product-list class="floating-controls-container floating-controls-container--inside floating-controls-container--on-hover">
                  <carousel-prev-button class="floating-controls-container__control" aria-controls="${carouselId}">
                    <button type="button" class="prev-next-button prev-next-button--prev circle-button hover:animate-icon-inline" disabled>
                      <span class="sr-only">{{ 'general.accessibility.previous' | t }}</span>
                      ${themeVariables.icons.arrowLeft}
                    </button>
                  </carousel-prev-button>

                  <scroll-carousel allow-drag class="product-list product-list--carousel scroll-area bleed" id="${carouselId}">
                    ${collectionItemsHtml}
                  </scroll-carousel>

                  <carousel-next-button class="floating-controls-container__control" aria-controls="${carouselId}">
                    <button type="button" class="prev-next-button prev-next-button--next circle-button hover:animate-icon-inline">
                      <span class="sr-only">{{ 'general.accessibility.next' | t }}</span>
                      ${themeVariables.icons.arrowRight}
                    </button>
                  </carousel-next-button>
                </product-list>
              `
            : ''
          }
        </x-drawer>
      </li>
    `;
  }
}

if (!customElements.get('all-ingredients')) {
  customElements.define('all-ingredients', AllIngredients);
}